import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Captions, Loader2, Music, Sparkles, Volume2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Switch } from "@/shared/ui/switch";
import { useBrandAssets } from "@/entities/brand-asset";
import { DragOverlayCanvas } from "@/shared/ui/overlay/DragOverlayCanvas";
import { type OverlayConfig } from "@/shared/lib/overlay/overlayConfig";
import { cn } from "@/shared/lib/utils";
import { isLandscape } from "@/shared/lib/video/aspect";
import type { VideoAssetDto, VideoComposeOptions, VideoTaskDto } from "../../api/videoService";
import {
  DEFAULT_SUBTITLE_STYLE,
  applySubtitleDrag,
  applySubtitlePosition,
  applySubtitleWidth,
  previewAnchorY,
  previewFontPx,
  previewOutlinePx,
  readSubtitleStyle,
  subtitleComposeOptions,
  subtitleStyleEqual,
  type SubtitleStyle,
} from "../../model/subtitleStyle";
import {
  applyLogoSelection,
  buildComposeOptions,
  composeStateEqual,
  readComposeMaterials,
  readInitialOverlay,
  readInitialToggles,
  resolveLogoPreviewUrl,
  resolveQrPreviewUrl,
  VIDEO_QR_OVERLAY_ENABLED,
  type ComposeToggles,
} from "../../model/composePanel";

export interface VideoComposePanelProps {
  task: VideoTaskDto;
  finalAsset: VideoAssetDto | null;
  /** overlay 拖拽底图(成片封面/首帧)。由 caller 经 pickBaseImage 算好且保证非空。 */
  baseImageUrl: string;
  applying: boolean;
  onApply: (options: VideoComposeOptions) => void | Promise<void>;
}

interface ToggleRowProps {
  id: keyof ComposeToggles;
  icon: typeof Volume2;
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  disabledHint: string;
  onToggle: (next: boolean) => void;
}

function ToggleRow({
  id,
  icon: Icon,
  label,
  hint,
  checked,
  disabled,
  disabledHint,
  onToggle,
}: ToggleRowProps) {
  return (
    <div
      onClick={() => !disabled && onToggle(!checked)}
      className={cn(
        "flex items-center gap-2.5 px-3 py-2 transition-colors",
        disabled ? "opacity-50" : "cursor-pointer hover:bg-muted/40"
      )}
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium leading-tight">{label}</div>
        <div className="text-2xs leading-tight text-muted-foreground">
          {disabled ? disabledHint : hint}
        </div>
      </div>
      {/* 阻止冒泡:点开关本身只触发一次,不再被行 onClick 二次取反 */}
      <span onClick={(e) => e.stopPropagation()}>
        <Switch
          id={`compose-${id}`}
          aria-label={label}
          checked={disabled ? false : checked}
          disabled={disabled}
          onCheckedChange={onToggle}
        />
      </span>
    </div>
  );
}


/** 三档分段选择器(字幕大小/位置/描边共用)。value 传空串 = 三档都不高亮(已被拖动接管)。 */
function SegRow({
  label,
  options,
  value,
  disabled,
  onPick,
}: {
  label: string;
  options: { v: string; t: string }[];
  value: string;
  disabled?: boolean;
  onPick: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="text-2xs text-muted-foreground">{label}</div>
      <div className="flex gap-0.5 rounded-md bg-muted p-0.5">
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            aria-pressed={value === o.v}
            aria-label={`${label}:${o.t}`}
            disabled={disabled}
            onClick={() => onPick(o.v)}
            className={cn(
              "flex-1 rounded px-1 py-1 text-2xs transition-colors",
              value === o.v
                ? "bg-background font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground",
              disabled && "opacity-50"
            )}
          >
            {o.t}
          </button>
        ))}
      </div>
    </div>
  );
}

const POSITION_LABEL: Record<string, string> = { top: "顶部", center: "居中", bottom: "底部" };

/**
 * 成片编辑的状态与两半 UI(画布 / 控件)。
 *
 * ★做成 hook 而不是 render-prop:D 版三列要把画布放中栏、控件放左栏,而那两栏之间还夹着
 *   播放器、下载按钮、这条片的参数 —— 这些与「成片编辑」毫无关系的东西不该挂在它的子树里
 *   (挂上去 = 编辑面板一旦不渲染,整页正文跟着消失;布局测试把它 stub 掉时当场暴露)。
 */
export function useComposeEditor({
  task,
  finalAsset: _finalAsset,
  baseImageUrl,
  applying,
  onApply,
}: Omit<VideoComposePanelProps, "task"> & { task: VideoTaskDto | null }) {
  const { data: brand, isLoading: brandLoading } = useBrandAssets();
  // ★task 可空:页面要在「加载中/出错」的早返回**之前**调这个 hook —— 放在早返回之后
  //   会让首帧(task 还没到)与次帧(task 到了)的 hook 数量不一样,React 直接抛
  //   「Rendered more hooks than during the previous render」整页白屏。
  //   单测抓不到:轮询 mock 一上来就给 task,那条早返回路径从没被走到过(真浏览器当场炸)。
  const payload = task?.requestPayload;
  const materials = useMemo(() => readComposeMaterials(payload), [payload]);

  const [toggles, setToggles] = useState<ComposeToggles>(() => readInitialToggles(payload));
  // brand 异步:settle 后才初始化 overlay + 记录基线(判断有无未应用改动),否则 logo/qr 会被锁成 null。
  const [overlay, setOverlay] = useState<OverlayConfig | null>(null);
  // VID-D:字幕版式(大小/位置/描边/拖出来的锚点与宽度)。payload 回显,改动与 overlay 同批应用。
  const [subtitle, setSubtitle] = useState<SubtitleStyle>(() => readSubtitleStyle(payload));
  const [subtitleBase, setSubtitleBase] = useState<SubtitleStyle>(() => readSubtitleStyle(payload));
  const [baseline, setBaseline] = useState<{
    toggles: ComposeToggles;
    overlay: OverlayConfig;
  } | null>(null);
  useEffect(() => {
    if (overlay == null && !brandLoading) {
      const init = readInitialOverlay(payload, brand);
      setOverlay(init);
      setBaseline({ toggles: readInitialToggles(payload), overlay: init });
      const sub = readSubtitleStyle(payload);
      setSubtitle(sub);
      setSubtitleBase(sub);
    }
  }, [overlay, brandLoading, brand, payload]);

  const setToggle = (k: keyof ComposeToggles) => (next: boolean) =>
    setToggles((t) => ({ ...t, [k]: next }));

  const hasBrand = (brand?.logos.length ?? 0) > 0 || (brand?.qrs.length ?? 0) > 0;
  const logoUrl = overlay ? (resolveLogoPreviewUrl(overlay, brand) ?? undefined) : undefined;
  const qrUrl = overlay ? (resolveQrPreviewUrl(overlay, brand) ?? undefined) : undefined;
  // VID-BRAND-1:二维码暂隐 —— 但老任务已存 QR 的回显仍在画布上,文案跟着实际有没有 QR 走。
  const qrVisible = VIDEO_QR_OVERLAY_ENABLED || overlay?.qr != null;
  const dragTargets = qrVisible ? "logo / 二维码" : "logo";
  const logos = brand?.logos ?? [];
  // 字幕预览取**本片第一句真口播**(不是编的样例):所见即所得的前提是看到的就是自己的话。
  const subtitleSample = (() => {
    const vo = payload?.voiceovers;
    const first = Array.isArray(vo) ? vo.find((v) => typeof v === "string" && v.trim()) : null;
    return typeof first === "string" ? first.trim() : "";
  })();
  // 画布预览高度(9:16 竖版按 340 宽算;仅用于把引擎的字号/描边口径缩到预览尺度)
  const canvasPreviewH = Math.round(340 * (isLandscape(task?.ratio ?? "9:16") ? 9 / 16 : 16 / 9));

  const dirty =
    (overlay != null && baseline != null && !composeStateEqual({ toggles, overlay }, baseline)) ||
    !subtitleStyleEqual(subtitle, subtitleBase);

  const handleApply = () => {
    if (!overlay) return;
    // ★字幕版式与开关/叠加**同一批**发出去:分两次发会让用户点一次只生效一半。
    onApply({ ...buildComposeOptions(toggles, materials, overlay), ...subtitleComposeOptions(subtitle) });
  };

  const canvasNode = (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">封面帧 · 拖拽叠加</span>
              {!hasBrand && (
                <Link
                  to="/tenant-admin/brand-assets"
                  className="text-xs text-primary underline-offset-2 hover:underline"
                >
                  配置品牌资产
                </Link>
              )}
            </div>
            {overlay && (
              <DragOverlayCanvas
                baseImageUrl={baseImageUrl}
                brandAssets={{ logoUrl, qrUrl }}
                value={overlay}
                onChange={setOverlay}
                readOnly={applying}
                /* VID-D:字幕与 logo 同画布所见即所得。没有口播的片不渲染整条带 —— 
                   那种片本来就没有字幕,画一条假的等于骗人。 */
                subtitle={
                  subtitleSample && toggles.subtitleEnabled && materials.hasVoice
                    ? {
                        anchorY: previewAnchorY(subtitle),
                        widthPct: subtitle.widthPct ?? 0.86,
                        sampleText: subtitleSample,
                        fontPx: previewFontPx(subtitle, canvasPreviewH),
                        outlinePx: previewOutlinePx(subtitle, canvasPreviewH),
                      }
                    : null
                }
                onSubtitleChange={
                  applying
                    ? undefined
                    : ({ anchorY, widthPct }) =>
                        setSubtitle((s0) =>
                          applySubtitleWidth(applySubtitleDrag(s0, anchorY), widthPct)
                        )
                }
              />
            )}
            {/**
             * VID-BRAND-1 的多 logo 选择条 —— 2026-08-19 老板走查「选 logo 那里尽量明显一些」后重做。
             *
             * ★真机实测的样子:6 个 **40×40** 小格贴在封面帧下沿,**没有任何可见标题**
             *   (只有 aria-label,读屏念得出、眼睛看不见),其中两格**整个是空白**
             *   (白底 logo 画在白底格子上),选中态只有 `ring-1` 在这个尺寸下看不出来,
             *   格子里不写名字、34px 的图标缩成一团认不出品牌。整排看着像装饰性缩略图条,
             *   不像一个「要选的东西」。
             * ★四处一起改才叫「明显」:可见标题 + 当前选中的名字 + 每格带名字 + ring-2 + 衬底。
             *   少任何一条,那两个白格子还是白格子。
             */}
            {overlay && logos.length > 0 && (
              <p data-testid="logo-picker-title" className="text-2xs font-medium">
                LOGO
                <span className="ml-1 font-normal text-muted-foreground">
                  {logos.find((l) => l.objectKey === overlay.logoUploadId)?.name ?? "未选"}
                  {logos.length > 1 ? " · 点下面换" : ""}
                </span>
              </p>
            )}
            {overlay && logos.length > 1 && (
              <div
                role="listbox"
                aria-label="选择上片的 logo"
                className="flex gap-1.5 overflow-x-auto pb-0.5"
              >
                {logos.map((l) => {
                  const selected = overlay.logoUploadId === l.objectKey;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      role="option"
                      data-testid={`logo-option-${l.id}`}
                      aria-selected={selected}
                      aria-label={`选用 logo「${l.name}」`}
                      title={l.name}
                      disabled={applying}
                      onClick={() => setOverlay(applyLogoSelection(overlay, l.objectKey))}
                      className={cn(
                        // bg-muted 是给**白底 logo** 的衬底:纯白格子上的白 logo 整格看不见。
                        "flex w-16 shrink-0 flex-col items-center gap-0.5 overflow-hidden rounded border bg-muted p-1 transition-colors",
                        selected
                          ? "border-primary ring-2 ring-primary"
                          : "border-border/60 hover:border-primary/50",
                        applying && "opacity-50"
                      )}
                    >
                      {l.signedUrl ? (
                        <img
                          src={l.signedUrl}
                          alt={l.name}
                          draggable={false}
                          className="h-9 w-full object-contain"
                        />
                      ) : (
                        <span className="flex h-9 items-center text-2xs text-muted-foreground">
                          {l.name.slice(0, 2)}
                        </span>
                      )}
                      {/* 名字必须写出来:图标缩到 36px 认不出是哪个品牌,白底的干脆看不见 */}
                      <span className="w-full truncate text-center text-2xs leading-tight">
                        {l.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-2xs leading-snug text-muted-foreground">
              在封面帧上拖动 {dragTargets} 调整位置与大小。
              {logos.length > 1 ? "多个 logo 点缩略图换。" : ""}
            </p>
          </div>
  );

  const controlsNode = (
          <div className="flex flex-col gap-3">
            <div className="overflow-hidden rounded-lg border divide-y">
              <ToggleRow
                id="voiceEnabled"
                icon={Volume2}
                label="声音"
                hint="口播配音轨"
                checked={toggles.voiceEnabled}
                disabled={!materials.hasVoice || applying}
                disabledHint="本片无口播"
                onToggle={setToggle("voiceEnabled")}
              />
              <ToggleRow
                id="subtitleEnabled"
                icon={Captions}
                label="字幕"
                hint="烧录口播字幕"
                checked={toggles.subtitleEnabled}
                disabled={!materials.hasVoice || applying}
                disabledHint="本片无口播文案"
                onToggle={setToggle("subtitleEnabled")}
              />
              <ToggleRow
                id="bgmEnabled"
                icon={Music}
                label="背景音乐"
                hint="混入 BGM"
                checked={toggles.bgmEnabled}
                disabled={!materials.hasBgm || applying}
                disabledHint="本片未配 BGM"
                onToggle={setToggle("bgmEnabled")}
              />
            </div>


            {/* ── VID-D 字幕版式:三档 + 画布上拖 ── */}
            {materials.hasVoice && toggles.subtitleEnabled && (
              <div data-testid="subtitle-style" className="space-y-2 rounded-lg border p-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">字幕版式</span>
                  {subtitle.anchorY != null && (
                    <button
                      type="button"
                      onClick={() => setSubtitle((s0) => applySubtitlePosition(s0, s0.position))}
                      className="text-2xs text-primary underline underline-offset-2"
                    >
                      回到{POSITION_LABEL[subtitle.position]}
                    </button>
                  )}
                </div>
                <SegRow
                  label="大小"
                  options={[
                    { v: "small", t: "小" },
                    { v: "medium", t: "中" },
                    { v: "large", t: "大" },
                  ]}
                  value={subtitle.fontScale}
                  disabled={applying}
                  onPick={(v) => setSubtitle((s0) => ({ ...s0, fontScale: v as SubtitleStyle["fontScale"] }))}
                />
                <SegRow
                  label="位置"
                  options={[
                    { v: "top", t: "顶部" },
                    { v: "center", t: "居中" },
                    { v: "bottom", t: "底部" },
                  ]}
                  value={subtitle.anchorY != null ? "" : subtitle.position}
                  disabled={applying}
                  onPick={(v) =>
                    setSubtitle((s0) => applySubtitlePosition(s0, v as SubtitleStyle["position"]))
                  }
                />
                <SegRow
                  label="描边"
                  options={[
                    { v: "none", t: "无" },
                    { v: "thin", t: "细" },
                    { v: "thick", t: "粗" },
                  ]}
                  value={subtitle.outline}
                  disabled={applying}
                  onPick={(v) => setSubtitle((s0) => ({ ...s0, outline: v as SubtitleStyle["outline"] }))}
                />
                <p className="text-2xs leading-snug text-muted-foreground">
                  {subtitle.anchorY != null
                    ? "位置已按你拖的来;点上面的档位可以回到标准位。"
                    : "也可以直接在画面上把字幕条上下拖,左右边可以拉宽窄。"}
                </p>
              </div>
            )}
            {/* ★纵排:D 版左栏只有 212px,横排会把「当前即最新成片」挤成两行贴着按钮(实拍确认) */}
            <div className="mt-auto flex flex-col gap-1.5">
              <span className="text-2xs text-muted-foreground">
                {applying ? "正在重新出片…" : dirty ? "有改动未应用" : "当前即最新成片"}
              </span>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={applying || !overlay || !dirty}
                className="w-full cursor-pointer"
              >
                {applying ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    重新合成中…
                  </>
                ) : (
                  "应用并重新合成"
                )}
              </Button>
            </div>
          </div>
  );

  return { canvas: canvasNode, controls: controlsNode, dragTargets, dirty };
}

/** 老样子:两半并排装在一张卡里(<lg 与其它调用方走这条)。 */
export function VideoComposePanel(props: VideoComposePanelProps) {
  const { canvas, controls, dragTargets } = useComposeEditor(props);
  return (
    <Card>
      <CardContent className="space-y-3.5 p-4 sm:p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div>
            <h3 className="text-sm font-semibold leading-tight">成片编辑</h3>
            <p className="text-xs text-muted-foreground">
              调开关 · 拖 {dragTargets},秒级重出片(不花积分)
            </p>
          </div>
        </div>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-[minmax(0,240px)_minmax(0,300px)]">
          {canvas}
          {controls}
        </div>
      </CardContent>
    </Card>
  );
}
